import type {ComponentProps} from "react";
import {useFormContext} from "react-hook-form";

import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import {FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import {FiltersPopup} from "@/src/modules/core/components";

import {
  VEHICLE_CHECK_OUTCOME_LABELS,
  VEHICLE_CHECK_OUTCOMES,
  VEHICLE_CHECK_RECOGNITION_STATUS_LABELS,
} from "../lib/constants";

type VehicleChecksFilterForm = {
  outcome?: {label: string; value: string} | null;
  recognition_status?: {label: string; value: string} | null;
  outside_perimeter?: {label: string; value: string} | null;
  outside_shift_properties?: {label: string; value: string} | null;
};

type VehicleChecksAdvancedFiltersProps = {
  filter: Record<string, unknown>;
  onApplyFilters: (filter: Record<string, unknown>) => void;
  filterTriggerSize?: ComponentProps<typeof FiltersPopup>["size"];
};

const RECOGNITION_STATUS_OPTIONS = [
  {label: VEHICLE_CHECK_RECOGNITION_STATUS_LABELS.succeeded, value: "succeeded"},
  {label: VEHICLE_CHECK_RECOGNITION_STATUS_LABELS.manual, value: "manual"},
  {label: VEHICLE_CHECK_RECOGNITION_STATUS_LABELS.unreadable, value: "unreadable"},
  {label: VEHICLE_CHECK_RECOGNITION_STATUS_LABELS.failed, value: "failed"},
];

const BOOLEAN_FILTER_OPTIONS = [
  {label: "Yes", value: "1"},
  {label: "No", value: "0"},
];

const OUTCOME_OPTIONS = VEHICLE_CHECK_OUTCOMES.map((value) => ({
  value,
  label: VEHICLE_CHECK_OUTCOME_LABELS[value],
}));

export function VehicleChecksAdvancedFilters({
  filter,
  onApplyFilters,
  filterTriggerSize,
}: VehicleChecksAdvancedFiltersProps) {
  const hasFilters = Boolean(
    filter.outcome ||
      filter.recognition_status ||
      filter.outside_perimeter !== undefined ||
      filter.outside_shift_properties !== undefined,
  );

  const booleanFilterValue = (key: "outside_perimeter" | "outside_shift_properties") => {
    const raw = filter[key];
    if (raw === 0 || raw === "0") return BOOLEAN_FILTER_OPTIONS[1];
    if (raw === 1 || raw === "1") return BOOLEAN_FILTER_OPTIONS[0];
    return null;
  };

  const defaultValues: VehicleChecksFilterForm = {
    outcome:
      typeof filter.outcome === "string"
        ? (OUTCOME_OPTIONS.find((option) => option.value === filter.outcome) ?? null)
        : null,
    recognition_status:
      typeof filter.recognition_status === "string"
        ? (RECOGNITION_STATUS_OPTIONS.find(
            (option) => option.value === filter.recognition_status,
          ) ?? null)
        : null,
    outside_perimeter: booleanFilterValue("outside_perimeter"),
    outside_shift_properties: booleanFilterValue("outside_shift_properties"),
  };

  const handleApply = (data: VehicleChecksFilterForm) => {
    const nextFilter: Record<string, unknown> = {};
    if (filter.q) nextFilter.q = filter.q;
    if (filter.context_property_id) {
      nextFilter.context_property_id = filter.context_property_id;
    }
    if (data.outcome?.value) nextFilter.outcome = data.outcome.value;
    if (data.recognition_status?.value) {
      nextFilter.recognition_status = data.recognition_status.value;
    }
    if (data.outside_perimeter?.value) {
      nextFilter.outside_perimeter = Number(data.outside_perimeter.value);
    }
    if (data.outside_shift_properties?.value) {
      nextFilter.outside_shift_properties = Number(data.outside_shift_properties.value);
    }
    onApplyFilters(nextFilter);
  };

  return (
    <FiltersPopup
      showBadge={hasFilters}
      defaultValues={defaultValues}
      onApply={handleApply}
      onClear={() =>
        onApplyFilters({
          ...(filter.q ? {q: filter.q} : {}),
          ...(filter.context_property_id
            ? {context_property_id: filter.context_property_id}
            : {}),
        })
      }
      size={filterTriggerSize}
    >
      <VehicleChecksFilters />
    </FiltersPopup>
  );
}

function VehicleChecksFilters() {
  const form = useFormContext<VehicleChecksFilterForm>();

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="outcome"
        render={({field}) => (
          <FormItem>
            <FormLabel>Outcome</FormLabel>
            <FormControl>
              <ComboboxGeneric<{label: string; value: string}>
                items={OUTCOME_OPTIONS}
                getItemKey={(item) => item.value}
                getItemLabel={(item) => item.label}
                isItemEqual={(item, selected) => item.value === selected?.value}
                placeholder="Select outcome"
                value={field.value ?? null}
                onChange={(item) => field.onChange(item)}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="recognition_status"
        render={({field}) => (
          <FormItem>
            <FormLabel>Recognition status</FormLabel>
            <FormControl>
              <ComboboxGeneric<{label: string; value: string}>
                items={RECOGNITION_STATUS_OPTIONS}
                getItemKey={(item) => item.value}
                getItemLabel={(item) => item.label}
                isItemEqual={(item, selected) => item.value === selected?.value}
                placeholder="Select status"
                value={field.value ?? null}
                onChange={(item) => field.onChange(item)}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="outside_perimeter"
        render={({field}) => (
          <FormItem>
            <FormLabel>Outside perimeter</FormLabel>
            <FormControl>
              <ComboboxGeneric<{label: string; value: string}>
                items={BOOLEAN_FILTER_OPTIONS}
                getItemKey={(item) => item.value}
                getItemLabel={(item) => item.label}
                isItemEqual={(item, selected) => item.value === selected?.value}
                placeholder="Any"
                value={field.value ?? null}
                onChange={(item) => field.onChange(item)}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="outside_shift_properties"
        render={({field}) => (
          <FormItem>
            <FormLabel>Outside shift properties</FormLabel>
            <FormControl>
              <ComboboxGeneric<{label: string; value: string}>
                items={BOOLEAN_FILTER_OPTIONS}
                getItemKey={(item) => item.value}
                getItemLabel={(item) => item.label}
                isItemEqual={(item, selected) => item.value === selected?.value}
                placeholder="Any"
                value={field.value ?? null}
                onChange={(item) => field.onChange(item)}
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}
